import type { NestedFilter, CommonSort } from '@@partner:types:graphql'
import type { RsuiteSortType } from '@@partner:types:app'

import React, { useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { 
  Row, 
  Col, 
  Grid,
  Pagination
} from 'rsuite'

import {
  CartOutline,
  TimeOutline,
  CubeOutline,
  CheckmarkCircleOutline
} from 'react-ionicons'

import OrdersTable from '@components/order/table/OrdersTable'
import OrdersFilter from '@components/order/table/OrdersFilter'
import StatCard from '@components/common/StatCard/StatCard'
import KanbanBoard from '@components/order/kanban/KanbanBoard'
import { groupOrdersByStatusGroups } from '@components/order/kanban/kanbanHelpers'
import type { KanbanStatusGroup } from '@components/order/kanban/kanbanHelpers'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { useFetchOrdersByStatus } from '@@partner:graphql/query/order/ordersByStatus.gql'
import { ORDERS_PER_PAGE_LIMIT } from '@@partner:config/index'
import { convertToInt } from '@@partner:helpers/util'
import { prepareOrdersFilter } from '@@partner:helpers/order'
import './AssemblingOrdersPage.scss'
import {
  ORDER_STATUS_PLACED,
  ORDER_STATUS_WAITING_FOR_PAYMENT,
  ORDER_STATUS_ASSEMBLY_IN_PROGRESS,
  ORDER_STATUS_ASSEMBLY_COMPLETE,
  ORDER_STATUS_WAITING_FOR_COURIER
} from '@@partner:config/order-status-codes'

// Статусы сборки для фильтра
const ASSEMBLING_STATUSES = [
  ORDER_STATUS_PLACED,
  ORDER_STATUS_ASSEMBLY_IN_PROGRESS,
  ORDER_STATUS_ASSEMBLY_COMPLETE,
  ORDER_STATUS_WAITING_FOR_PAYMENT,
  ORDER_STATUS_WAITING_FOR_COURIER
]

// Группы статусов для колонок Kanban
const ASSEMBLING_KANBAN_GROUPS: KanbanStatusGroup[] = [
  {
    key: 'placed',
    label: 'Размещён',
    statuses: [ ORDER_STATUS_PLACED ]
  },
  {
    key: 'in_progress',
    label: 'Собирается',
    statuses: [ ORDER_STATUS_ASSEMBLY_IN_PROGRESS ]
  },
  {
    key: 'complete',
    label: 'Собран',
    statuses: [ ORDER_STATUS_ASSEMBLY_COMPLETE, ORDER_STATUS_WAITING_FOR_PAYMENT ]
  },
  {
    key: 'waiting_courier',
    label: 'Ожидает курьера',
    statuses: [ ORDER_STATUS_WAITING_FOR_COURIER ]
  }
]

export default function AssemblingPage () {
  const { brandSlug } = useParams<any>()
  const [ page, setPage ] = useState(1)
  const [ userFilter, setUserFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })
  const [ sort, setSort ] = useState<CommonSort>({
    fieldName: 'id',
    direction: 0
  })

  // Комбинируем базовый фильтр по статусам сборки с пользовательским фильтром
  const filter = useMemo<NestedFilter>(() => {
    const baseFilter: NestedFilter = {
      fields: [ 'status' ],
      op: 'contains',
      value: ASSEMBLING_STATUSES
    }
    
    // Если пользовательский фильтр пустой (unwrap), используем только базовый
    if (userFilter.op === 'unwrap') {
      return { and: [ baseFilter ] }
    }
    
    // Иначе комбинируем
    return {
      and: [ baseFilter, userFilter ]
    }
  }, [ userFilter ])

  const { data, loading } = useFetchOrders({
    filter,
    allActive: true,
    sort,
    pager: {
      page: 1,
      limit: 1000  // Большое значение для получения всех заказов в Kanban
    }
  })

  // Получаем статистику по статусам
  const { data: ordersByStatusData } = useFetchOrdersByStatus()

  const orders = useMemo(() => data?.orders?.edges || [], [ data ])
  const pageInfo = data?.orders?.pageInfo
  const showPagination = pageInfo?.lastPage !== 1

  // Группировка заказов по группам статусов для Kanban
  const ordersByGroup = useMemo(() => {
    return groupOrdersByStatusGroups(orders, ASSEMBLING_KANBAN_GROUPS)
  }, [ orders ])

  // Подготовка данных для статистики
  const statsMap = useMemo(() => {
    const map: Record<number, number> = {}
    ordersByStatusData?.ordersByStatus?.forEach(group => {
      map[ group.status ] = group.count
    })
    return map
  }, [ ordersByStatusData ])

  const waitingOrders = statsMap[ ORDER_STATUS_PLACED ] || 0
  const inProgressOrders = (statsMap[ ORDER_STATUS_WAITING_FOR_PAYMENT ] || 0) + (statsMap[ ORDER_STATUS_ASSEMBLY_IN_PROGRESS ] || 0)
  const readyOrders = (statsMap[ ORDER_STATUS_ASSEMBLY_COMPLETE ] || 0) + (statsMap[ ORDER_STATUS_WAITING_FOR_COURIER ] || 0)
  const totalOrders = waitingOrders + inProgressOrders + readyOrders

  const handlePageSelect = useCallback((selectedPage: number | string) => {
    setPage(convertToInt(selectedPage))
  }, [])

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setUserFilter(updatedFilter)
    setPage(1)
  }, [])

  const handleSortColumn = useCallback((sortColumn: string, sortType: RsuiteSortType | undefined) => {
    setSort({
      fieldName: sortColumn,
      direction: sortType === 'desc' ? 0 : 1
    })
  }, [])

  // Фильтрация по клику на карточку
  const handleStatCardClick = useCallback((statuses: number[]) => {
    setUserFilter({
      fields: [ 'status' ],
      op: 'contains',
      value: statuses
    })
    setPage(1)
  }, [])

  const handleShowAll = useCallback(() => {
    setUserFilter({
      fields: [ 'id' ],
      op: 'unwrap'
    })
    setPage(1)
  }, [])

  return (
    <div className="AssemblingOrdersPage">
      <h1 className="u-heading">Управление заказами в сборке</h1>

      {/* Блоки статистики */}
      <Grid fluid style={{ marginBottom: 20 }}>
        <Row>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Всего в сборке"
              count={totalOrders}
              icon={<CartOutline color="#fff" height="32px" width="32px" />}
              color="#607d8b"
              onClick={handleShowAll}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Ожидают сборки"
              count={waitingOrders}
              icon={<TimeOutline color="#fff" height="32px" width="32px" />}
              color="#ff9800"
              onClick={() => handleStatCardClick([ ORDER_STATUS_PLACED ])}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="В процессе сборки"
              count={inProgressOrders}
              icon={<CubeOutline color="#fff" height="32px" width="32px" />}
              color="#9c27b0"
              onClick={() => handleStatCardClick([ ORDER_STATUS_WAITING_FOR_PAYMENT, ORDER_STATUS_ASSEMBLY_IN_PROGRESS ])}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <StatCard
              title="Готовы к отправке"
              count={readyOrders}
              icon={<CheckmarkCircleOutline color="#fff" height="32px" width="32px" />}
              color="#4caf50"
              onClick={() => handleStatCardClick([ ORDER_STATUS_ASSEMBLY_COMPLETE, ORDER_STATUS_WAITING_FOR_COURIER ])}
            />
          </Col>
        </Row>
      </Grid>

      <OrdersFilter onFilterChanged={handleFilterChanged} />

      <KanbanBoard
        brandSlug={brandSlug}
        ordersByGroup={ordersByGroup}
        loading={loading}
        groups={ASSEMBLING_KANBAN_GROUPS}
      />
    </div>
  )
}
